'use client';

import { c as _c } from "react/compiler-runtime";
import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, useRef } from 'react';
import { AnimatedBackground } from '@/components/AnimatedBackground';
import Card from '@/components/service-pages/MediaParallaxCards/Card';
import { MediaParallaxCardsDto } from '@/components/service-pages/MediaParallaxCards/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import css from './style.module.scss';
type VideoSources = {
  mov?: string;
  webm?: string;
  mp4?: string;
};
const VIDEO_SOURCE_META: {
  key: keyof VideoSources;
  type: string;
}[] = [{
  key: 'mov',
  type: 'video/quicktime; codecs="hvc1"'
}, {
  key: 'webm',
  type: 'video/webm'
}, {
  key: 'mp4',
  type: 'video/mp4'
}];
const DEFAULT_VIDEO_SOURCES: VideoSources = {
  mov: '/assets/video/mov/website-prod-page/focus-matters-chub.mov',
  webm: '/assets/video/webm/website-prod-page/focus-matters-chub.webm'
};
const GREEN_THEME_VIDEO_SOURCES: VideoSources = {
  mov: '/assets/video/mov/branding-page/green-chub.mov',
  webm: '/assets/video/webm/branding-page/green-chub.webm'
};
interface Props {
  data: MediaParallaxCardsDto;
  theme?: 'purple' | 'green';
  withNumbers?: boolean;
}
const MediaParallaxCards: FC<Props> = t0 => {
  const $ = _c(43);
  const {
    data,
    theme: t1,
    withNumbers: t2
  } = t0;
  const theme = t1 === undefined ? "purple" : t1;
  const withNumbers = t2 === undefined ? false : t2;
  const wrapperRef = useRef(null);
  const withNumbersCards = data?.withNumbersCards ?? withNumbers;
  const isIndustry = data.isIndustry ?? false;
  const mainTheme = data?.theme ?? theme;
  const resolvedVideo = mainTheme === "green" ? GREEN_THEME_VIDEO_SOURCES : DEFAULT_VIDEO_SOURCES;
  const shouldLoadVideo = useLazyVideoSource(wrapperRef);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [wrapperRef];
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  useViewportVideosPlayback(t3);
  let t4;
  if ($[1] !== isIndustry) {
    t4 = () => {
      const matchMedia = gsap.matchMedia();
      matchMedia.add({
        isDesktop: "(min-width: 800px)",
        isMobile: "(max-width: 799px)"
      }, () => {
        const titleLines = wrapperRef.current?.querySelectorAll(`.${css.title} p`);
        const subtitle = wrapperRef.current?.querySelector(`.${css.subtitle}`);
        if (titleLines?.length) {
          gsap.set(titleLines, {
            opacity: 0,
            y: 40,
            filter: "blur(10px)"
          });
        }
        if (subtitle) {
          gsap.set(subtitle, {
            opacity: 0,
            y: 20
          });
        }
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: titleLines,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        });
        if (titleLines?.length) {
          tl.to(titleLines, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 1.1,
            ease: "power3.out",
            stagger: 0.15
          });
        }
        if (subtitle) {
          tl.to(subtitle, {
            opacity: 1,
            y: 0,
            duration: 1,
            ease: "power3.out"
          }, "-=0.8");
        }
      });
      matchMedia.add("(min-width: 799px)", () => {
        const cards = wrapperRef.current?.querySelectorAll(`.${css.card}`);
        if (!cards) {
          return;
        }
        const yPercentByCardsCount = cards.length === 4 ? [50, -30, 70, -25] : isIndustry ? [50, -10, -25] : [70, -30, 40];
        cards.forEach((card, i) => {
          gsap.to(card, {
            yPercent: yPercentByCardsCount[i] ?? 30,
            ease: "none",
            scrollTrigger: {
              trigger: wrapperRef.current,
              start: "top bottom",
              end: "bottom top",
              scrub: true
            }
          });
        });
        return () => {
          gsap.set(cards, {
            yPercent: 0,
            clearProps: "transform"
          });
        };
      });
    };
    $[1] = isIndustry;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      scope: wrapperRef
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  useGSAP(t4, t5);
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = clsx(css.wrapper, "container");
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const t7 = isIndustry ? "h2-industry" : "h2";
  let t8;
  if ($[5] !== data.title) {
    t8 = <StrapiRichContent content={data.title} />;
    $[5] = data.title;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  let t9;
  if ($[7] !== t7 || $[8] !== t8) {
    t9 = <Typography uppercase={true} variant={t7} tag="h2" className={css.title}>{t8}</Typography>;
    $[7] = t7;
    $[8] = t8;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = isIndustry ? "body-m" : "body-l";
  let t11;
  if ($[10] !== data.subtitle || $[11] !== t10) {
    t11 = <Typography variant={t10} tag="p" responsive={false} className={css.subtitle}>{data.subtitle}</Typography>;
    $[10] = data.subtitle;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== resolvedVideo || $[14] !== shouldLoadVideo) {
    t12 = shouldLoadVideo ? VIDEO_SOURCE_META.map(t13 => {
      const {
        key,
        type
      } = t13;
      const src = resolvedVideo[key];
      return src ? <source key={key} src={src} type={type} /> : null;
    }) : null;
    $[13] = resolvedVideo;
    $[14] = shouldLoadVideo;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t12) {
    t13 = <div className={css.videoWrapper}><video loop={true} playsInline={true} muted={true} controls={false} preload="none">{t12}</video></div>;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const t14 = isIndustry && css.industryCards;
  const t15 = isIndustry && data.cards.length === 3 && css.industryCardsThree;
  let t16;
  if ($[18] !== t14 || $[19] !== t15) {
    t16 = clsx(css.cards, t14, t15);
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  if ($[21] !== data.cards || $[22] !== isIndustry || $[23] !== mainTheme || $[24] !== withNumbersCards) {
    let t18;
    if ($[26] !== isIndustry || $[27] !== mainTheme || $[28] !== withNumbersCards) {
      t18 = (cardData, index) => <Card key={index} isIndustry={isIndustry} className={css.card} theme={mainTheme} data={{
        ...cardData,
        number: withNumbersCards ? `0${index + 1}` : undefined
      }} />;
      $[26] = isIndustry;
      $[27] = mainTheme;
      $[28] = withNumbersCards;
      $[29] = t18;
    } else {
      t18 = $[29];
    }
    t17 = data.cards.map(t18);
    $[21] = data.cards;
    $[22] = isIndustry;
    $[23] = mainTheme;
    $[24] = withNumbersCards;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[30] !== t16 || $[31] !== t17) {
    t18 = <div className={t16}>{t17}</div>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t11 || $[34] !== t13 || $[35] !== t18 || $[36] !== t9) {
    t19 = <div className={css.container}>{t9}{t11}{t13}{t18}</div>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t18;
    $[36] = t9;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== mainTheme) {
    t20 = <AnimatedBackground theme={mainTheme} />;
    $[38] = mainTheme;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== t19 || $[41] !== t20) {
    t21 = <section ref={wrapperRef} className={t6}>{t19}{t20}</section>;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  return t21;
};
export default MediaParallaxCards;